import {
    CheckoutState,
    CourseAddOn,
    CourseOffer,
    OccurrenceAddOn,
    OccurrenceOffer,
    OrderCheckoutState,
} from '@shared/types';
import { PublicEducator, PublicOnDemandCourse } from '@cademy-package/sdk-component-types';
import { FunctionComponent, PropsWithChildren, createContext, useContext, useMemo } from 'react';
import { ResponseSubmitter } from '../../useCheckout';
import { CademyError } from '@shared/domain-shared';
import { CheckoutContext, CheckoutContextProvider } from '..';
import { PaymentOptions, usePaymentOptions } from '../../data/usePaymentOptions';

type OrderCheckoutContext = CheckoutContext<
    OrderCheckoutState,
    PublicOnDemandCourse,
    PublicEducator
> & {
    offers: Array<CourseOffer | OccurrenceOffer>;
    addons: Array<CourseAddOn | OccurrenceAddOn>;
    offersById: { [offerId: string]: CourseOffer | OccurrenceOffer };
    addonsById: { [addonId: string]: CourseAddOn | OccurrenceAddOn };
};

const OrderCheckoutContext = createContext<Error | OrderCheckoutContext>(
    new Error(
        'Attempted to useOrderCheckoutContext in a Component not wrapped in OrderCheckoutContextProvider'
    )
);

export type OrderCheckoutContextProviderProps = PropsWithChildren<{
    course: PublicOnDemandCourse;
    educator: PublicEducator;
    checkoutState: OrderCheckoutState;
    isLoading: boolean;
    submitResponse: ResponseSubmitter<OrderCheckoutState>;
    error: CademyError | null;
}>;
export const OrderCheckoutContextProvider: FunctionComponent<OrderCheckoutContextProviderProps> = ({
    course,
    educator,
    checkoutState,
    submitResponse,
    isLoading,
    error,
    children,
}) => {
    const offers = useMemo(() => {
        return course.offers;
    }, [course]);

    const offersById = useMemo(() => {
        return offers.reduce((offersById, offer) => {
            return { ...offersById, [offer._id]: offer };
        }, {});
    }, [offers]);

    const addons = useMemo(() => {
        return course.add_ons || [];
    }, [course]);

    const addonsById = useMemo(() => {
        return addons.reduce((addonsById, addon) => {
            return { ...addonsById, [addon._id]: addon };
        }, {});
    }, [addons]);

    const { data: allPaymentOptions, isLoading: isLoadingPaymentOptions } = usePaymentOptions({
        courseId: checkoutState.courseId,
        educatorId: checkoutState.educatorId,
    });

    const paymentOptions = useMemo((): PaymentOptions | undefined => {
        if (!allPaymentOptions) {
            return undefined;
        }
        return allPaymentOptions;
    }, [allPaymentOptions]);

    const orderContextValue = useMemo((): OrderCheckoutContext => {
        return {
            course,
            educator,
            checkoutState,
            submitResponse,
            isLoading: isLoading || isLoadingPaymentOptions,
            error,
            addons,
            addonsById,
            offers,
            offersById,
            paymentOptions: paymentOptions || {
                card: { enabled: true },
                paypal: { enabled: false },
                invoice: { enabled: false },
                manual: { enabled: false },
                klarna: { enabled: false },
            },
        };
    }, [
        course,
        educator,
        checkoutState,
        submitResponse,
        isLoading,
        error,
        offers,
        offersById,
        addons,
        addonsById,
        paymentOptions,
        isLoadingPaymentOptions,
    ]);

    const checkoutContextValue = useMemo((): CheckoutContext => {
        return {
            course,
            educator,
            checkoutState,
            submitResponse: submitResponse as ResponseSubmitter<CheckoutState>,
            isLoading: isLoading || isLoadingPaymentOptions,
            error,
            paymentOptions: paymentOptions || {
                card: { enabled: true },
                paypal: { enabled: false },
                invoice: { enabled: false },
                manual: { enabled: false },
                klarna: { enabled: false },
            },
        };
    }, [
        course,
        educator,
        checkoutState,
        submitResponse,
        isLoading,
        error,
        paymentOptions,
        isLoadingPaymentOptions,
    ]);

    return (
        <OrderCheckoutContext.Provider value={orderContextValue}>
            <CheckoutContextProvider value={checkoutContextValue}>
                {children}
            </CheckoutContextProvider>
        </OrderCheckoutContext.Provider>
    );
};

export const useOrderCheckoutContext = (): OrderCheckoutContext => {
    const context = useContext(OrderCheckoutContext);
    if (context instanceof Error) {
        throw context;
    }
    return context;
};
